"use client";

import React from "react";
import { Resource } from "@/config/resources";
import { Action } from "@/types";
import { usePermissions } from "@/hooks/use-permissions";

interface PermissionGateProps {
  resource: Resource;
  action: Action;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * A component that renders its children only if the user has the specified permission.
 *
 * @example
 * <PermissionGate resource="products" action="read">
 *   <ProductList />
 * </PermissionGate>
 *
 * @example With fallback
 * <PermissionGate
 *   resource="orders"
 *   action="update"
 *   fallback={<p>You don't have permission to update orders</p>}
 * >
 *   <OrderEditForm />
 * </PermissionGate>
 */
export function PermissionGate({
  resource,
  action,
  children,
  fallback = null,
}: PermissionGateProps) {
  const { checkPermission } = usePermissions();

  // If the user doesn't have permission, show the fallback or nothing
  if (!checkPermission(resource, action)) {
    return fallback ? <>{fallback}</> : null;
  }

  // User has permission, render the children
  return <>{children}</>;
}

/**
 * A component that renders its children only if the user has field-level permission.
 */
interface FieldPermissionGateProps {
  resource: Resource;
  field: string;
  action: "read" | "write";
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function FieldPermissionGate({
  resource,
  field,
  action,
  children,
  fallback = null,
}: FieldPermissionGateProps) {
  const { checkFieldPermission } = usePermissions();

  // If the user doesn't have permission, show the fallback or nothing
  if (!checkFieldPermission(resource, field, action)) {
    return fallback ? <>{fallback}</> : null;
  }

  // User has permission, render the children
  return <>{children}</>;
}
